import { lazy, Suspense } from "react";
import { Outlet } from "react-router-dom";
import { ModulePermissionGuard } from "src/auth/guard";
import { AppGuard, MotorGuard } from "src/auth/guard/guard-helpers";
import AuthGuard from "src/auth/guard/auth-guard";

import { LoadingScreen } from "src/components/loading-screen";
import DashboardLayout from "src/layouts/dashboard";

// ----------------------------------------------------------------------

// HOME
const DashboardHomeView = lazy(
  () => import("src/modules/dashboard/components/dashboard-home"),
);

// USERS
const AdminUserPage = lazy(
  () => import("src/modules/admin-users/components/admin-users"),
);
const CreateAdminUserPage = lazy(
  () => import("src/modules/admin-users/components/create-admin-users"),
);
const EditAdminUserPage = lazy(
  () => import("src/modules/admin-users/components/create-admin-users"),
);

// APP USERS
const AppUsersPage = lazy(
  () => import("src/modules/app-users/components/app-users"),
);
const AppUserFormPage = lazy(
  () => import("src/modules/app-users/components/app-user-form"),
);

// Produtos
const BenefitsPage = lazy(
  () => import("src/modules/benefits/components/benefits"),
);
const BenefitFormPage = lazy(
  () => import("src/modules/benefits/components/benefit-form"),
);

// OFFERS
const OffersPage = lazy(() => import("src/modules/offers/components/offers"));

// CONVERSION RATES
const ConversionRatesPage = lazy(
  () => import("src/modules/conversion-rates/components/conversion-rates"),
);
const ConversionRateFormPage = lazy(
  () => import("src/modules/conversion-rates/components/conversion-rate-form"),
);

const RulesPage = lazy(() => import("src/modules/rules/components/rules"));
const CreateRulePage = lazy(
  () => import("src/modules/rules/components/create-rule"),
);
const EditRulePage = lazy(
  () => import("src/modules/rules/components/create-rule"),
);

// VARIABLES
const VariablesPage = lazy(
  () => import("src/modules/variables/components/variables"),
);

// REDEEMS
const RedeemsView = lazy(
  () => import("src/modules/redeems/components/redeems"),
);

const TermsOfUsePage = lazy(
  () => import("src/modules/terms-of-use/components/terms-of-use"),
);
const CreateTermsOfUsePage = lazy(
  () => import("src/modules/terms-of-use/components/create-terms-of-use"),
);

// PRIVACY AND POLICY
const PrivacyPolicyPage = lazy(
  () => import("src/modules/privacy-policy/components/privacy-policy"),
);
const CreatePrivacyPolicyPage = lazy(
  () => import("src/modules/privacy-policy/components/create-privacy-policy"),
);

// SELLERS
const SellersPage = lazy(
  () => import("src/modules/sellers/components/sellers"),
);

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: "dashboard",
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      // HOME
      {
        path: "home",
        element: <DashboardHomeView />,
      },

      // ADMIN USERS
      {
        path: "admin-user",
        children: [
          {
            element: (
              <ModulePermissionGuard module="adminUsers" action="view">
                <AdminUserPage />
              </ModulePermissionGuard>
            ),
            index: true,
          },
          {
            path: "create",
            element: (
              <ModulePermissionGuard module="adminUsers" action="create">
                <CreateAdminUserPage />
              </ModulePermissionGuard>
            ),
          },
          {
            path: ":id",
            element: (
              <ModulePermissionGuard module="adminUsers" action="edit">
                <EditAdminUserPage />
              </ModulePermissionGuard>
            ),
          },
        ],
      },

      // USUÁRIOS DO APP
      {
        path: "app-users",
        children: [
          {
            element: (
              <AppGuard module="appUsers" action="view">
                <AppUsersPage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: ":id",
            element: (
              <AppGuard module="appUsers" action="edit">
                <AppUserFormPage />
              </AppGuard>
            ),
          },
        ],
      },

      // PRODUCTS
      {
        path: "products",
        children: [
          {
            element: (
              <AppGuard module="appProducts" action="view">
                <BenefitsPage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: "create",
            element: (
              <AppGuard module="appProducts" action="create">
                <BenefitFormPage />
              </AppGuard>
            ),
          },
          {
            path: ":id",
            element: (
              <AppGuard module="appProducts" action="edit">
                <BenefitFormPage />
              </AppGuard>
            ),
          },
        ],
      },

      //REDEEMS
      {
        path: "redeems",
        element: (
          <AppGuard module="appBenefitRedeems" action="view">
            <RedeemsView />
          </AppGuard>
        ),
      },

      // OFFERS
      {
        path: "offers",
        element: (
          <AppGuard module="appBanners" action="view">
            <OffersPage />
          </AppGuard>
        ),
      },

      {
        path: "terms",
        children: [
          {
            element: (
              <AppGuard module="appTermsOfUse" action="view">
                <TermsOfUsePage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: "create",
            element: (
              <AppGuard module="appTermsOfUse" action="create">
                <CreateTermsOfUsePage />
              </AppGuard>
            ),
          },
          {
            path: ":id",
            element: (
              <AppGuard module="appTermsOfUse" action="edit">
                <CreateTermsOfUsePage />
              </AppGuard>
            ),
          },
        ],
      },

      // PRIVACY POLICY
      {
        path: "privacy",
        children: [
          {
            element: (
              <AppGuard module="appPrivacyAndPolicy" action="view">
                <PrivacyPolicyPage />
              </AppGuard>
            ),
            index: true,
          },
          {
            path: "create",
            element: (
              <AppGuard module="appPrivacyAndPolicy" action="create">
                <CreatePrivacyPolicyPage />
              </AppGuard>
            ),
          },
          {
            path: ":id",
            element: (
              <AppGuard module="appPrivacyAndPolicy" action="edit">
                <CreatePrivacyPolicyPage />
              </AppGuard>
            ),
          },
        ],
      },

      // CONVERSION RATES
      {
        path: "conversionrate",
        children: [
          {
            element: (
              <MotorGuard module="motorConversionRates" action="view">
                <ConversionRatesPage />
              </MotorGuard>
            ),
            index: true,
          },
          {
            path: "create",
            element: (
              <MotorGuard module="motorConversionRates" action="create">
                <ConversionRateFormPage />
              </MotorGuard>
            ),
          },
          {
            path: ":id",
            element: (
              <MotorGuard module="motorConversionRates" action="edit">
                <ConversionRateFormPage />
              </MotorGuard>
            ),
          },
        ],
      },

      // RULES
      {
        path: "rules",
        children: [
          {
            element: (
              <MotorGuard module="motorRules" action="view">
                <RulesPage />
              </MotorGuard>
            ),
            index: true,
          },
          {
            path: "create",
            element: (
              <MotorGuard module="motorRules" action="create">
                <CreateRulePage />
              </MotorGuard>
            ),
          },
          {
            path: ":id",
            element: (
              <MotorGuard module="motorRules" action="edit">
                <EditRulePage />
              </MotorGuard>
            ),
          },
        ],
      },

      // VARIABLES
      {
        path: "variables",
        element: (
          <MotorGuard module="motorVariables" action="view">
            <VariablesPage />
          </MotorGuard>
        ),
      },

      // SELLERS
      {
        path: "sellers",
        element: <SellersPage />,
      },
    ],
  },
];
